/**
 * use-mock-participants - Composable for managing mock participants
 *
 * Provides:
 * - Reactive list of mock participants
 * - Add/remove participant logic
 * - Role change and voting actions
 * - Session room auto-detection from route
 * - Max participant limit enforcement
 */

import { computed, ref, shallowRef, triggerRef, type ComputedRef, type ShallowRef } from 'vue';
import { useRoute } from 'vue-router';
import { createMockParticipantClient } from '@/dev-tools/services/MockParticipantClient.js';
import type { MockParticipantClient, UseMockParticipants } from '../types.ts';
import type { RequiredUserVote, UserRole } from '@pokerly/contracts/events/common';
import { DEFAULT_ESTIMATION_OPTIONS } from '@/config/session-config.js';

const MAX_MOCK_PARTICIPANTS = 25;
const RANDOM_VOTE_MIN_DELAY_MS = 200;
const RANDOM_VOTE_MAX_DELAY_MS = 3000;

const participants: ShallowRef<MockParticipantClient[]> = shallowRef([]);
const isVotingAll = ref(false);
export function useMockParticipants(): UseMockParticipants {
    const route = useRoute();

    /**
     * Auto-detect session room from URL path: /session/:room -> room
     */
    const currentSessionRoom: ComputedRef<string | null> = computed(() => {
        const match = route.path.match(/^\/session\/([^/]+)/);
        return match?.[1] ?? null;
    });

    /**
     * Check if we can add more participants
     */
    const canAddMore: ComputedRef<boolean> = computed(() => participants.value.length < MAX_MOCK_PARTICIPANTS);

    /**
     * Generate a unique ID for the mock participant
     */
    function generateId(): string {
        return `mock-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    }

    /**
     * Add a new mock participant
     */
    async function addParticipant(): Promise<void> {
        if (!canAddMore.value) {
            throw new Error(`Maximum mock participants (${MAX_MOCK_PARTICIPANTS}) reached`);
        }

        if (!currentSessionRoom.value) {
            throw new Error('Not in a session. Cannot add mock participant.');
        }

        let client: MockParticipantClient | null = null;
        try {
            client = createMockParticipantClient(generateId());
            participants.value.push(client);
            triggerRef(participants);
            // Connect to session
            await client.connect(currentSessionRoom.value);
            // Trigger reactivity so computeds reading inner refs (status, role) re-evaluate
            triggerRef(participants);
        } catch {
            if (!client) return;
            client.disconnect();
            // Remove the specific participant if connection failed
            participants.value = participants.value.filter((p) => p.id !== client!.id);
        }
    }

    /**
     * Remove a mock participant
     */
    function removeParticipant(id: string): void {
        const index = participants.value.findIndex((p) => p.id === id);
        if (index !== -1) {
            const participant = participants.value[index];
            if (participant) {
                participant.disconnect();
            }
            participants.value.splice(index, 1);
            triggerRef(participants);
        }
    }

    /**
     * Remove all mock participants
     */
    function removeAll(): void {
        participants.value.forEach((p) => p.disconnect());
        participants.value = [];
    }

    /**
     * Change a participant's role
     */
    async function setRole(participantId: string, role: UserRole): Promise<void> {
        const client = participants.value.find((p) => p.id === participantId);
        if (!client) return;
        await client.setRole(role);
        triggerRef(participants);
    }

    /**
     * Cast a vote for a participant
     */
    async function vote(participantId: string, value: RequiredUserVote): Promise<void> {
        const client = participants.value.find((p) => p.id === participantId);
        if (!client) return;
        await client.vote(value);
        triggerRef(participants);
    }

    /**
     * Clear a participant's vote (local reset)
     */
    function clearVote(participantId: string): void {
        const client = participants.value.find((p) => p.id === participantId);
        if (!client) return;
        client.clearVote();
        triggerRef(participants);
    }

    /**
     * Trigger a random vote for every connected voter mock participant.
     * Each vote is sent after a random delay to simulate real users.
     * Errors are caught per-participant so a single failure doesn't break the batch.
     */
    async function voteAllRandom(): Promise<void> {
        const voters = participants.value.filter((p) => p.status.value === 'connected' && p.role.value === 'voter');
        if (voters.length === 0) return;

        // Exclude '?' from the random vote options
        const numericOptions = DEFAULT_ESTIMATION_OPTIONS.filter((o) => typeof o === 'number');

        isVotingAll.value = true;

        const votePromises = voters.map((participant) => {
            const delay =
                Math.random() * (RANDOM_VOTE_MAX_DELAY_MS - RANDOM_VOTE_MIN_DELAY_MS) + RANDOM_VOTE_MIN_DELAY_MS;
            const randomValue = numericOptions[Math.floor(Math.random() * numericOptions.length)]!;

            return new Promise<void>((resolve) => {
                setTimeout(async () => {
                    try {
                        await participant.vote(randomValue);
                        triggerRef(participants);
                    } catch (err) {
                        console.error(`[voteAllRandom] vote failed for ${participant.id}:`, err);
                    }
                    resolve();
                }, delay);
            });
        });

        await Promise.allSettled(votePromises);
        isVotingAll.value = false;
    }

    return {
        participants,
        canAddMore,
        isVotingAll: computed(() => isVotingAll.value),
        addParticipant,
        removeParticipant,
        removeAll,
        setRole,
        vote,
        clearVote,
        voteAllRandom,
        currentSessionRoom,
    };
}
